// Added by Sejune Oh on 2019-05-17
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { makeAutoObservable } from 'mobx'
import { createRef } from 'react'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Define
import { APP_CLIENT_KEY_COOKIE, StopServerReason } from '../helper/define'

// Util
import { getCookie, setCookie } from '../helper/util'

// Logger
import Logger from '../helper/Logger'

import Intl from '../helper/Intl'

// Root Store
import Rootstore from './index'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Interface
import { DialogContentProps } from '../components/common/Dialog'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
export default class CLAppStore {
  _clientKey?: string
  runnable: boolean = false
  doSignOutTask: any
  redirectToMgmtTask: any
  redirectToLicenseTask: any
  isPromptRequired: boolean = false
  promptMessage: string = ''
  handleDialogConfirm?: () => Promise<{}>
  handleDialogCancel?: () => Promise<{}>

  root?: Rootstore
  dialogRef = createRef<any>()

  constructor(root: Rootstore) {
    this.root = root
    makeAutoObservable(this)
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // get app server token by auth token
  get appToken(): string {
    if (this.root!.userAuth!.userInfo) {
      return this.getAppToken(this.root!.userAuth!.userInfo!.username!)
    } else {
      Logger.errLog('Non authorized user')
      return ''
    }
  }

  // get client key
  get clientKey(): string {
    if (!this._clientKey) this.setClientKeyCookie()

    return this._clientKey!
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  init = () => {
    this.runnable = false
  }

  setClientKeyCookie = () => {
    let clientKey = getCookie(APP_CLIENT_KEY_COOKIE)

    if (!clientKey) {
      clientKey = [...Array(24)].map((i) => (~~(Math.random() * 36)).toString(36)).join('')
      setCookie({ name: APP_CLIENT_KEY_COOKIE, value: clientKey, expHours: 24 * 7 }) // 7 days
    }

    this._clientKey = clientKey
  }

  setTask = (signOutTask: Function, redirectToMgmt?: Function, redirectToLicense?: Function) => {
    this.doSignOutTask = () => {
      return new Promise((resolve) => {
        signOutTask()
        resolve(true)
      })
    }

    this.redirectToMgmtTask = () => {
      return new Promise((resolve) => {
        if (redirectToMgmt) redirectToMgmt()
        resolve(true)
      })
    }

    this.redirectToLicenseTask = () => {
      return new Promise((resolve) => {
        if (redirectToLicense) redirectToLicense()
        resolve(true)
      })
    }
  }

  setCLAppRunnable = () => {
    this.runnable = true
  }

  setDialog = (dialogInfo: DialogContentProps, confirm: () => Promise<{}>, cancel?: () => Promise<{}>) => {
    this.dialogRef.current!.setDialogInfo({ ...dialogInfo, disableBackdropClick: true })

    this.handleDialogConfirm = confirm

    if (cancel) {
      this.handleDialogCancel = cancel
    }
  }

  setMessageBeforeWebPageUnload = ({ message }: { message: string }) => {
    this.promptMessage = message
    this.isPromptRequired = message !== ''
  }

  clearMessageBeforeWebPageUnload = () => {
    this.promptMessage = ''
    this.isPromptRequired = false
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  initializeUserSession = async (reqCPUScore: string) => {
    try {
      // license가 invalid하더라도 worklist가 보여야 한다.
      // worklist가 app으로부터 독립이 되기 전까지 license check를 하지 않도록 한다. by Byeongsoo Kim
      // if (this.root!.license!.licenseKey === '') {
      //     this.setDialog(
      //         {
      //             title: 'common.warning',
      //             description: 'clAppInit.notAvaLicense',
      //             hideCancelBtn: !this.root!.userAuth!.isAdminUser,
      //             confirmBtn: 'common.logout',
      //             cancelBtn: 'clAppInit.mvToLicenseBtn',
      //         },
      //         this.doSignOutTask,
      //         this.redirectToLicenseTask
      //     )
      //     this.dialogRef.current!.handleOpen()
      //     return
      // }

      // Check previous app session
      await this._checkPrevUserSession()
      await this._checkAvailableNode(reqCPUScore)

      this.setCLAppRunnable()
    } catch (err) {
      Logger.errLog(err)
      this.dialogRef.current!.handleOpen()
    }
  }

  stopAppServer = async (
    forced: boolean = false,
    reason: StopServerReason = StopServerReason.USER_LOGOUT,
    userName?: string,
    onlyMySession?: boolean
  ): Promise<boolean> => {
    // 자신이 생성한 서버가 아니면 종료하지 않는다.
    const isMyUserSession = await this._checkValidUserSession();
    if (!forced && isMyUserSession ) {
      return Promise.resolve(false)
    }

    // 강제종료시 자신이 생성한 서버만 종료 하는 경우
    if (forced && onlyMySession && !isMyUserSession) {
      return Promise.resolve(false)
    }

    try {
      // stop app server
      const appToken = userName ? this.getAppToken(userName) : null
      await this.root!.registry!.stopCntAppServer(appToken || this.appToken, reason)
    } catch (e) {
      Logger.errLog('<CLAppStore> :: Cant stop server')
      console.log(e)
      Logger.errLog('=====================================')
    }

    return Promise.resolve(false)
  }

  /**
   * Viewer에서 로그아웃 하는 경우에 관한 처리.
   * 아래 조건문은 Dashboard를 Enable하게 두고 Worklist에 들어가지 않은 상태로 로그아웃 했을 때
   * window.clapp.isElectron 등을 사용할 수 없어서 사용함
   */
  viewerLogout = () => {
    // @ts-ignore
    if (window && window.process && window.process.type) {
      // eslint-disable-next-line no-undef
      const { ipcRenderer } = __non_webpack_require__('electron')
      if (ipcRenderer) {
        ipcRenderer.send('viewer-logout')
        ipcRenderer.send('screen-sharing-stop')
      }
    }
  }

  closeAnotherAppServerAndStart = (): Promise<{}> => {
    return new Promise(async (resolve, reject) => {
      // Stop app server
      // Chck Shutdown reasons in dng-golang.../common/defines.go
      await this.stopAppServer(true, StopServerReason.NEW_SESSION_STARTED)

      const sleep = (ms: number) => {
        return new Promise((resolve) => setTimeout(resolve, ms))
      }

      for (let i = 0; i < 100; i++) {
        await sleep(1000)
        if (!(await this.isAppServerRunnging())) {
          break
        }
      }

      // Check wheter app server is still running
      if (await this.isAppServerRunnging()) {
        Logger.errLog('<CLAppStore> :: Cant stop app server')

        this.setDialog(
          {
            title: Intl.getIntlText({ id: 'htmljs_react.global.clappstore.dialog_title_warning' }),
            description: Intl.getIntlText({ id: 'htmljs_react.global.clappstore.dialog_desc_cant_stop_app' }),
            hideCancelBtn: !this.root!.userAuth!.isAdminUser,
            confirmBtn: Intl.getIntlText({ id: 'htmljs_react.global.clappstore.dialog_btn_logout' }),
            cancelBtn: Intl.getIntlText({ id: 'htmljs_react.global.clappstore.dialog_btn_mv_to_mgmt' }),
          },
          this.doSignOutTask,
          this.redirectToMgmtTask
        )
        return reject()
      }

      this.setCLAppRunnable()
      resolve(true)
    })
  }

  getAppToken = (userName: string) => {
    return `_app_${userName}`
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  _checkPrevUserSession = async () => {
    const valid = await this._checkValidUserSession()

    if (valid) return Promise.resolve(true)

    this.setDialog(
      {
        title: Intl.getIntlText({ id: 'htmljs_react.global.clappstore.dialog_title_warning' }),
        description: Intl.getIntlText({ id: 'htmljs_react.global.clappstore.dialog_desc_confirm_login' }),
        confirmBtn: Intl.getIntlText({ id: 'htmljs_react.global.clappstore.dialog_btn_continue' }),
        cancelBtn: Intl.getIntlText({ id: 'htmljs_react.global.clappstore.dialog_btn_cancel' }),
      },
      this.closeAnotherAppServerAndStart,
      this.doSignOutTask
    )
    return Promise.reject()
  }

  _checkAvailableNode = async (reqCPUScore: string) => {
    // check available node
    if (!(await this._hasAvaialbleNode(reqCPUScore))) {
      Logger.errLog('<CLAppStore> :: Not available node')

      this.setDialog(
        {
          title: Intl.getIntlText({ id: 'htmljs_react.global.clappstore.dialog_title_warning' }),
          description: Intl.getIntlText({ id: 'htmljs_react.global.clappstore.dialog_desc_max_session' }),
          hideCancelBtn: !this.root!.userAuth!.isAdminUser,
          confirmBtn: Intl.getIntlText({ id: 'htmljs_react.global.clappstore.dialog_btn_logout' }),
          cancelBtn: Intl.getIntlText({ id: 'htmljs_react.global.clappstore.dialog_btn_mv_to_mgmt' }),
        },
        this.doSignOutTask,
        this.redirectToMgmtTask
      )
      return Promise.reject()
    }

    return Promise.resolve(true)
  }

  /**
   * 현재 계정으로 이미 활성화되어 있는 app server를 찾고, 있을 경우, client key를 대조한다.
   * 활성화된 app server가 없거나 활성화된 app server가 있고 client key가 동일할 경우 true를 리턴함.
   */
  _checkValidUserSession = async (): Promise<boolean> => {
    try {
      const { data } = await this.root!.registry!.getCntServer(this.appToken)
      if (data.additional_data) {
        // extract client key
        const values = data.additional_data
          .split(',')
          .map((v: string) => v.split('='))
          .filter((v: Array<string>) => v.length === 2)
          .map((v: Array<string>) => {
            return { key: v[0], value: v[1] }
          })
          .filter((v: any) => v !== null && v.key === 'client-key')
          .map((v: any) => v.value)

        if (values.length > 0) {
          const prev_client_key = values[0]
          return prev_client_key === this.clientKey
        }
      }
    } catch (e) {
      Logger.errLog('<CLAppStore> :: _checkValidUserSession')
      console.log(e)
      Logger.errLog('=====================================')
    }

    return true
  }

  isAppServerRunnging = async (userName?: string): Promise<boolean> => {
    try {
      const appToken = userName ? `_app_${userName}` : null
      const { status } = await this.root!.registry!.getCntServer(appToken || this.appToken)
      return status === 200
    } catch (e) {
      Logger.errLog('<CLAppStore> :: isAppServerRunnging')
      console.log(e)
      Logger.errLog('=====================================')
    }

    return false
  }

  _hasAvaialbleNode = async (reqCPUScore: string): Promise<boolean> => {
    try {
      const {
        data: { available },
      } = await this.root!.node!.getAvailableNode({
        required_score_cpu: parseInt(reqCPUScore),
        app_session_only: true,
        client_key: this.clientKey,
      })
      return available!
    } catch (e) {
      Logger.errLog('<CLAppStore> :: isAppServerRunnging')
      console.log(e)
      Logger.errLog('=====================================')
    }
    return false
  }
}

